import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PLACEMENT_PILOT } from '../data/content';
import { X, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';

export default function PlacementPilot() {
  const [lightbox, setLightbox] = useState(-1);

  const next = () => setLightbox((v) => (v + 1) % PLACEMENT_PILOT.previews.length);
  const prev = () => setLightbox((v) => (v - 1 + PLACEMENT_PILOT.previews.length) % PLACEMENT_PILOT.previews.length);

  return (
    <section className="py-24 md:py-32 bg-[#1F2B73] text-white relative overflow-hidden">
      <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#293891] blur-3xl opacity-40" />
      <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#E63329]/30 blur-3xl opacity-40" />

      <div className="container-x relative">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6 min-w-0">
            <div className="inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-white/60">
              <span className="w-7 h-px bg-red" />
              {PLACEMENT_PILOT.eyebrow}
            </div>
            <div className="mt-6">
              <h2 className="font-display text-[44px] sm:text-[64px] md:text-[92px] leading-none break-words">
                {PLACEMENT_PILOT.title}
              </h2>
              <div className="font-display text-white/50 text-2xl mt-1">{PLACEMENT_PILOT.by}</div>
            </div>
            <div className="mt-8 font-display text-[22px] md:text-[26px] leading-snug text-white/90 max-w-xl">
              {PLACEMENT_PILOT.headline}
            </div>
            <p className="mt-6 text-[15.5px] text-white/70 leading-relaxed max-w-xl">{PLACEMENT_PILOT.description}</p>

            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
              {PLACEMENT_PILOT.stats.map((s) => (
                <div key={s.label} className="border-t border-white/10 pt-4">
                  <div className="font-display text-[32px]">{s.value}</div>
                  <div className="text-[10.5px] tracking-[0.2em] uppercase font-semibold text-white/60 mt-2">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6 min-w-0">
            <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-white/60">
              The 5-Round Journey
            </div>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {PLACEMENT_PILOT.rounds.map((r, i) => (
                <div
                  key={r}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl border border-white/10 bg-white/[0.03]"
                >
                  <span className="w-8 h-8 rounded-full bg-red flex items-center justify-center text-white font-display text-[13px] flex-shrink-0">
                    {i + 1}
                  </span>
                  <span className="text-[14px] text-white/90">{r}</span>
                </div>
              ))}
            </div>

            <div className="mt-10 grid sm:grid-cols-2 gap-4">
              {PLACEMENT_PILOT.features.map((f, i) => (
                <motion.div
                  key={f.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  className="p-5 rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur"
                >
                  <Sparkles size={16} className="text-red" />
                  <div className="mt-3 font-display text-[16px]">{f.title}</div>
                  <p className="mt-2 text-[13px] text-white/60 leading-relaxed">{f.body}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-20">
          <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-white/60">Inside the platform</div>
          <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PLACEMENT_PILOT.previews.map((p, i) => (
              <motion.button
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                onClick={() => setLightbox(i)}
                className="group text-left rounded-2xl overflow-hidden border border-white/10 bg-white/[0.03]"
              >
                <div className="aspect-[16/10] overflow-hidden bg-black/40">
                  <img
                    src={p.url}
                    alt={p.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4">
                  <div className="text-[10px] uppercase tracking-widest text-white/50">Preview {String(i + 1).padStart(2, '0')}</div>
                  <div className="font-display text-[16px] mt-1">{p.title}</div>
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {lightbox >= 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-6"
            onClick={() => setLightbox(-1)}
          >
            <button className="absolute top-6 right-6 text-white/80 hover:text-white" onClick={() => setLightbox(-1)}>
              <X size={30} />
            </button>
            <button className="absolute left-6 md:left-10 text-white/60 hover:text-white" onClick={(e) => { e.stopPropagation(); prev(); }}>
              <ChevronLeft size={40} />
            </button>
            <button className="absolute right-6 md:right-10 text-white/60 hover:text-white" onClick={(e) => { e.stopPropagation(); next(); }}>
              <ChevronRight size={40} />
            </button>
            <motion.img
              key={lightbox}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.25 }}
              src={PLACEMENT_PILOT.previews[lightbox].url}
              alt={PLACEMENT_PILOT.previews[lightbox].title}
              className="max-w-[92vw] max-h-[85vh] rounded-xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
